import { useState, useEffect } from "react";
import { CameraProvider } from "../../context/CameraContext";
import { useTheme } from "../../context/ThemeContext";
import GridLayoutManager from "./enhance_components/GridLayoutManager";
import CameraToolbarPro from "./enhance_components/CameraToolbarPro";
import CameraHealthHeader from "./enhance_components/CameraHealthHeader";
import KeyboardShortcuts from "./enhance_components/KeyboardShortcuts";
import TourMode from "./enhance_components/TourMode";
import CameraLogViewer from "./enhance_components/CameraLogViewer";
import { useCameraContext } from "../../context/CameraContext";

/**
 * EnhancedCameraWorkspace
 * ========================================
 * Enterprise-grade live monitoring workspace
 */
export default function EnhancedCameraWorkspace({
  cameras,
  streamStatus,
  canManageCameras,
  onAddCamera,
  onEditCamera,
  onDeleteCamera,
  updateStreamStatus,
  orgKey = "default",
}) {
  return (
    <CameraProvider
      cameras={cameras}
      streamStatus={streamStatus}
      orgKey={orgKey}
    >
      <WorkspaceInner
        canManageCameras={canManageCameras}
        onAddCamera={onAddCamera}
        onEditCamera={onEditCamera}
        onDeleteCamera={onDeleteCamera}
        updateStreamStatus={updateStreamStatus}
      />
    </CameraProvider>
  );
}

function WorkspaceInner({
  canManageCameras,
  onAddCamera,
  onEditCamera,
  onDeleteCamera,
  updateStreamStatus,
}) {
  const { theme } = useTheme();
  const {
    filteredCameras,
    streamStatus,
    logCamera,
    setLogCamera,
  } = useCameraContext();

  // Layout state
  const [layout, setLayout] = useState("grid-4");
  const [fullscreen, setFullscreen] = useState(false);
  const [focusedCamera, setFocusedCamera] = useState(null);
  const [tourActive, setTourActive] = useState(false);

  // Calculate health stats with priority-based status
  const getCameraStatus = (camera) => {
    // Priority 1: User disabled (inactive)
    if (camera.is_active === false) return "inactive";
    
    // Priority 2: Check connection status (prefer connection_status over streamStatus)
    if (camera.connection_status === "online") return "online";
    if (streamStatus[camera.id] === "online") return "online";
    
    // Priority 3: Default to offline
    return "offline";
  };

  // Calculate counts using reduce to avoid overlapping
  const stats = filteredCameras.reduce((acc, camera) => {
    const status = getCameraStatus(camera);
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, { online: 0, offline: 0, inactive: 0 });

  const total = filteredCameras.length;
  const online = stats.online;
  const offline = stats.offline;
  const inactive = stats.inactive;
  const healthScore = total > 0 ? Math.round((online / total) * 100) : 0;


  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyPress = (e) => {
      // Don't trigger if user is typing in an input
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        return;
      }

      // ESC - Exit fullscreen or tour
      if (e.key === "Escape") {
        if (tourActive) {
          setTourActive(false);
        } else if (fullscreen) {
          setFullscreen(false);
          if (document.fullscreenElement) {
            document.exitFullscreen();
          }
        } else if (focusedCamera) {
          setFocusedCamera(null);
        }
      }
      
      // F - Toggle fullscreen
      if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        toggleFullscreen();
      }
      
      // T - Toggle tour
      if (e.key === "t" || e.key === "T") {
        e.preventDefault();
        setTourActive(!tourActive);
      }

      // Number keys 1-4 for layouts
      if (e.key >= "1" && e.key <= "4" && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        const layoutMap = {
          "1": "spotlight",
          "2": "grid-4",
          "3": "grid-9",
          "4": "grid-16",
        };
        if (layoutMap[e.key]) {
          setLayout(layoutMap[e.key]);
        }
      }
    };

    window.addEventListener("keydown", handleKeyPress);
    return () => window.removeEventListener("keydown", handleKeyPress);
  }, [fullscreen, focusedCamera, tourActive]);

  // Fullscreen API
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setFullscreen(true);
    } else {
      document.exitFullscreen();
      setFullscreen(false);
    }
  };

  // Handle fullscreen change (user pressing ESC in fullscreen)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  return (
    <div className={`space-y-6 ${fullscreen ? 'fixed inset-0 z-50 p-6 bg-black' : ''}`}>
      {/* Health Header */}
      {!fullscreen && !tourActive && (
        <CameraHealthHeader
          total={total}
          online={online}
          offline={offline}
          inactive={inactive}
          healthScore={healthScore}
        />
      )}

      {/* Professional Toolbar */}
      {!tourActive && (
        <CameraToolbarPro
          canManageCameras={canManageCameras}
          onAddCamera={onAddCamera}
          layout={layout}
          setLayout={setLayout}
          fullscreen={fullscreen}
          toggleFullscreen={toggleFullscreen}
          tourActive={tourActive}
          setTourActive={setTourActive}
        />
      )}

      {/* Main Grid */}
      {!tourActive && (
        <GridLayoutManager
          cameras={filteredCameras}
          streamStatus={streamStatus}
          layout={layout}
          focusedCamera={focusedCamera}
          setFocusedCamera={setFocusedCamera}
          updateStreamStatus={updateStreamStatus}
          onEditCamera={onEditCamera}
          onDeleteCamera={onDeleteCamera}
          canManageCameras={canManageCameras}
          onViewLogs={setLogCamera}
        />
      )}

      {/* Tour Mode - Fullscreen Overlay */}
      {tourActive && (
        <TourMode
          cameras={filteredCameras}
          onClose={() => setTourActive(false)}
          updateStreamStatus={updateStreamStatus}
        />
      )}

      {/* Keyboard Shortcuts Help */}
      <KeyboardShortcuts />

      {/* Logs Modal */}
      {logCamera && (
        <CameraLogViewer
          cameraId={logCamera.id}
          onClose={() => setLogCamera(null)}
        />
      )}
    </div>
  );
}
